import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Button, Box, Card, Container, Stack, Typography, TextField, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormControl, RadioGroup, Radio, FormControlLabel, Grid } from '@mui/material'
import { Clear as CancelIcon, Done as ApproveIcon } from '@mui/icons-material';
import { DataGrid, GridEditInputCell, GridCellEditStopReasons, useGridApiContext } from '@mui/x-data-grid';
import CustomDataGridToolbar from '../../../common/customDataGridToolbar';
import { toast } from 'react-toastify';

import { loaderService } from '../../../common/custom-loader/loader-service';
import { getLocalStorageItem } from '../../../common/localStorageUtils';
import { GET_STAKEHOLDER_NOTIFICATION, SAVE_STAKEHOLDER_NOTIFICATION, GET_STAKEHOLDER_CATEGORY, FETCH_RATE_CONFIRMATION_BY_URN, UPDATE_RATE_CONFIRMATION } from '../../../common/URLs';
import jwt from 'jwt-decode';
import { styled } from '@mui/material/styles';
import { DEMAND_VALIDATIONS, validateAmount } from '../../../common/commonValidations';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { CustomButton } from '../../../common/custom-button/customButton';
import { useTranslation } from 'react-i18next';
import { ConfirmationPopover } from '../../../common/commonPopovers/confirmationPopover';
import { format } from 'date-fns';
import { handleNumberFieldFocus } from '../../../common/commonFunction';
import { SubmitIcon } from '../../../common/icons';
import { FileUploadMUI } from '../../../common/fileUpload/fileUploadMUI';
import { viewdownloadDocument } from '../knowledgeManagement/knowledgeMangementApi';
import apiClient from '../../../axios';

function StakeholderRateApproval() {

    const [approveDialog, setApproveDialog] = useState(false);
    const handleApproveDialogOpen = () => setApproveDialog(true);
    const handleApproveDialogClose = () => setApproveDialog(false);

    const StyledTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
        },
    }));

    function EditInputCell(props) {
        const { error, id, value: valueProp, field } = props;
        const [value, setValue] = useState(valueProp);
        const apiRef = useGridApiContext();

        const handleChange = (event) => {
            const newValue = event.target.value;
            apiRef.current.setEditCellValue({ id, field, value: newValue });
            setValue(newValue);
        };

        useEffect(() => {
            setValue(valueProp);
        }, [valueProp]);

        return (
            <StyledTooltip open={!!error} title={error}>
                <GridEditInputCell {...props} value={value} onChange={handleChange} />
            </StyledTooltip>
        );
    }

    const { t } = useTranslation();
    const [urn, setURN] = useState();
    const [ratesData, setRatesData] = useState([]);
    console.log("ratesData", ratesData)
    const [selectedRows, setSelectedRows] = useState([]);

    // -----------------API Calls-------------------

    useEffect(() => {
        if (getLocalStorageItem('id_token')) {
            const decoded = jwt(getLocalStorageItem('id_token'));
            const URN = decoded.extension_URN;
            setURN(URN);
        }
    }, [getLocalStorageItem('id_token')]);

    useEffect(() => {
        if (urn) {
            fetchRateConfirmationByUrn();
        }

    }, [urn])

    function fetchRateConfirmationByUrn() {
        if (urn) {
            loaderService.showLoader()
            apiClient
                .post(FETCH_RATE_CONFIRMATION_BY_URN,
                    {
                        "urnNumber": urn,
                    },
                    {
                        headers: {
                            Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
                        }
                    }, null
                )
                .then((response) => {
                    setRatesData(response?.data.map(item => ({ ...item, statusInitial: item?.status })));
                    loaderService.hideLoader()
                })
                .catch(function (error) {
                    console.log(error);
                    loaderService.hideLoader()
                    toast.error(error.message);
                });
        }
    }

    const approveRate = (approvalData) => {
        loaderService.showLoader();
        apiClient.post(UPDATE_RATE_CONFIRMATION,
            [...approvalData],
            {
                headers: {
                    Authorization: 'Bearer ' + getLocalStorageItem('id_token'),
                }
            }, null
        )
            .then((response) => {
                if (response.data.status === "SUCCESS") {
                    toast.success(response.data.description);
                    fetchRateConfirmationByUrn();
                    setSelectedRows([]);
                }
                else if (response.data.status === "FAILURE") {
                    toast.error(response.data.description);
                }
                else toast.error("Error! Please try again")
                loaderService.hideLoader();

            }).catch(function (error) {
                loaderService.hideLoader();
                toast.error(error.message);
            });
    }

    const columns = [
        {
            field: 'urnNumber',
            headerName: "Stakeholder URN",
            width: 150
        },
        {
            field: 'stakeholderName',
            headerName: "Stakeholder Name",
            width: 200,
        },
        {
            field: 'landName',
            headerName: "Land Name",
            width: 180,
        },
        {
            field: 'totalQuantity',
            headerName: "Total Qty(in MT)",
            width: 180,
        },
        {
            field: 'committedQuantity',
            headerName: "Committed Qty(in MT)",
            width: 180,
        },
        {
            field: 'validFrom',
            headerName: "Valid From",
            width: 110,
            renderCell: (params) => {
                return (
                    <>
                        {format(new Date(params?.row?.validFrom), 'dd/MM/yyyy')}
                    </>
                )
            }
        },
        {
            field: 'validTo',
            headerName: "Valid To",
            width: 110,
            renderCell: (params) => {
                return (
                    <>
                        {format(new Date(params?.row?.validTo), 'dd/MM/yyyy')}
                    </>
                )
            }
        },
        {
            field: 'expiredDate',
            headerName: "Expiry Date",
            width: 110,
            renderCell: (params) => {
                return (
                    <>
                      {params?.row?.expiredDate && format(new Date(params?.row?.expiredDate), 'dd/MM/yyyy')}
                    </>
                )
            }
        },
        {
            field: 'itemRate',
            headerName: "Item Rate",
            width: 100,
        },

        {
            field: 'proposeRate',
            headerName: "Proposed Rate",
            width: 150,
            renderCell: (params) => {
                return (
                    <>
                        {params.row.status === "Proposed" ? <TextField size="small" type="number" onFocus={handleNumberFieldFocus}
                            onKeyDown={(evt) => (evt.key === 'e' || evt.key === 'E' || evt.key === '-' || evt.key === '+') && evt.preventDefault()}
                            value={params.row?.proposeRate} disabled={params?.row?.statusInitial != null || params.row?.isExpired == "Y"} variant="standard" onChange={(e) => setRatesData(prevItems => prevItems?.map(item => item.id === params?.row?.id ? { ...item, proposeRate: validateAmount(e.target.value) } : item))}></TextField> : params.value}
                    </>
                )
            }
        },
        {
            field: 'action',
            headerName: "Action",
            width: 450,
            renderCell: (params) => {
                return (
                    <FormControl>
                        <RadioGroup
                            row
                            aria-labelledby="demo-radio-buttons-group-label"
                            name="radio-buttons-group2"
                        >
                            <FormControlLabel value="Accepted" checked={params?.row?.status === "Accepted"} disabled={params?.row?.statusInitial != null || params.row?.isExpired == "Y"} onChange={(e) => radioChange(e, params.row.id)} control={<Radio size="small" />} label="Approve" />
                            <FormControlLabel value="Rejected" checked={params?.row?.status === "Rejected"} disabled={params?.row?.statusInitial != null || params.row?.isExpired == "Y"} onChange={(e) => radioChange(e, params.row.id)} control={<Radio size="small" />} label="Reject" />
                            <FormControlLabel value="Proposed" checked={params?.row?.status === "Proposed"} disabled={params?.row?.statusInitial != null || params.row?.isExpired == "Y"} onChange={(e) => radioChange(e, params.row.id)} control={<Radio size="small" />} label="Propose New Rate" />
                        </RadioGroup>
                    </FormControl>
                );
            }
        },
        {
            field: 'comment',
            headerName: "Comment",
            width: 300,
        },
        {
            field: 'fileName',
            headerName: "File",
            width: 300,
            renderCell: (params) => {
                return (
                    <div className="Rescview-file-link-common">
                    <Typography onClick={(e) => viewdownloadDocument(params?.row?.rateCardDocumentMasterEnity?.[0]?.documentGuid, params?.row?.rateCardDocumentMasterEnity?.[0]?.documentName, "View")} >
                        {params?.row?.rateCardDocumentMasterEnity?.[0]?.documentName}
                    </Typography>
                    </div>
                );
            }
        },
    ];

    const handleSubmit = () => {
        handleApproveDialogOpen();
    }

    const onConfirmAction = () => {
        handleApproveDialogClose()
        const filteredData = ratesData?.filter(item => item.status && !item.statusInitial)
        const approvaldata = filteredData?.map(({ statusInitial, ...rest }) => ({
            ...rest
        }));
        approveRate(approvaldata);
    }

    const radioChange = (e, id) => {
        setRatesData(prevItems => prevItems?.map(item => item.id === id ? { ...item, status: e.target.value } : item))
    }

    return (
        <>
            <Container maxWidth="xxl">
                <Stack direction={{ xs: 'column', sm: 'row' }} mb={1} justifyContent="space-between">
                    <Typography variant="h4" gutterBottom>
                        Pending Rate Card Confirmation
                    </Typography>
                    {/* <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" mb={1}>
                        <><Stack direction="row" spacing={1}>
                            <Button variant="contained" disabled={selectedRows.length < 1} onClick={() => handleAction("Accepted")} startIcon={<ApproveIcon />}>
                                Accept
                            </Button>
                            <Button variant="contained" disabled={selectedRows.length < 1} onClick={() => handleAction("Rejected")} startIcon={<CancelIcon />}>
                                Reject
                            </Button>
                        </Stack></>
                    </Stack> */}
                </Stack>
                <Card>
                    <Box sx={{
                        height: "auto",
                        width: '100%',
                        '& .MuiDataGrid-cell--editable': {
                            bgcolor: (theme) =>
                                theme.palette.mode === 'dark' ? '#376331' : 'rgb(217 243 190)',
                        },
                        '& .Mui-error': {
                            bgcolor: (theme) => `rgb(126,10,15, ${theme.palette.mode === 'dark' ? 0 : 0.1})`,
                            color: (theme) => theme.palette.error.main,
                            height: '-webkit-fill-available'
                        },
                    }}
                    >
                        <DataGrid
                            rows={ratesData}
                            columns={columns}
                            pageSize={10}
                            getRowHeight={() => 'auto'}
                            autoHeight
                            // checkboxSelection
                            // onSelectionModelChange={(ids) => {
                            //     const selectedIDs = new Set(ids);
                            //     const selectedRowData = ratesData.filter((row) =>
                            //         selectedIDs.has(row.id.toString())
                            //     )
                            //     setSelectedRows(selectedRowData);
                            // }}
                            // isRowSelectable={(params) => params.row.status ? false : true}
                            components={{
                                Toolbar: CustomDataGridToolbar,
                            }}
                            componentsProps={{ toolbar: { rows: ratesData, columns: columns, fileName: "RatesConfirmation.xlsx" } }}
                        />
                    </Box>
                </Card>
                <Grid item xs={12} lg={12} display='flex' justifyContent="flex-end">
                    <Button endIcon={<SubmitIcon />} sx={{ minWidth: 100, mt: 2, ml: 2 }} variant="contained" onClick={handleSubmit}>Submit</Button>
                </Grid>
            </Container>
            {/* <Dialog
                open={openValidationDialog}
                aria-labelledby="responsive-dialog-title"
            >
                <DialogContent>
                    <DialogContentText>
                        Enter actual daily capacity for selected rows
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <CustomButton title="Ok" onClick={() => handleDialogClose()} />
                </DialogActions>
            </Dialog> */}
            <ConfirmationPopover onOpen={approveDialog} onConfirm={onConfirmAction} onClose={handleApproveDialogClose} action="Submit" />
            </>
    )
}

export default StakeholderRateApproval;